
/**
 * What an e-book IS, and the helpers that build one.
 *
 * Extracted on 2026-08-30, for the reason `storyBuilder.ts` and
 * `reportBuilder.ts` were: the entries live in `content/ebooks/*.ts` now and are
 * pulled in by an EAGER glob, which evaluates them while `ebooks.ts` is still
 * initialising. A globbed file importing `ebook()` from `ebooks.ts` would read
 * an uninitialised binding — a `ReferenceError` at prerender, after typecheck
 * has passed. Importing from here, which imports nothing back, cannot do that.
 */

/**
 * The Impact Labs e-books, and the page each one opens.
 *
 * The WordPress import brought `/impact-labs/ebooks/` across as **seventeen
 * loose image + heading pairs** — a cover, then a title, then the next cover —
 * so `LegacyPage` drew it as seventeen full-width figures stacked down a 768px
 * column with nothing clickable anywhere on it. On digitopia.co the same list is
 * a grid of cards and every card opens that e-book's own page
 * (`/impact-labs/ebooks/agentic-ai/`). Asked for on 2026-08-27 with a screenshot
 * of that grid: *"ebooklar bu şekilde tıklanabilir kutucuklar olmalı ve
 * kutucuklar kendi sayfasına gitmeli"*.
 *
 * Built exactly like `content/reports.ts`, which answered the same request for
 * the reports index the day before — a typed catalogue and a designed template,
 * not seventeen imported JSON files, because an e-book page is a two-column hero
 * with a cover and a download button and the block model in `content/blocks.ts`
 * has no shape for one.
 *
 * **The covers are the imported files, not new media.** All seventeen are
 * already in `public/legacy/` with WebP variants in `content/imported/images.json`
 * — they are what the index was drawing — so the grid costs no new bytes and
 * nothing had to be attached.
 *
 * Two honest gaps remain, both riding on `check` so a production build cannot
 * publish them quietly (`ebookChecks()` feeds the manifest, same as the reports'):
 *
 *  * **Only two slugs are confirmed.** The e-book pages themselves were never
 *    imported and the redirect table does not know them, so the rest are derived
 *    from the title — the part before the colon, kebab-cased, which is what
 *    produces `agentic-ai` from "Agentic AI: A Practical Guide" and
 *    `transform-better-e-book` from "Transform Better E-Book". Those two are the
 *    two we can check, and the rule reproduces both. Where it is wrong for one of
 *    the other fifteen, an inbound link to digitopia.co's own slug will 404 after
 *    the switch — which is why each one says so in amber.
 *  * **None has its own summary.** Every page carries the series description,
 *    which is true of the series, and says so.
 *
 * The third gap — fifteen e-books with no download destination — closed on
 * 2026-08-27, when marketing supplied the bucket URL for every one of them
 * (*"bu linkleri ilgili ebooklara download olarak koy"*). All seventeen now have
 * a file, so `stillToCome()` no longer prints that clause for anybody.
 */
// TYPE-ONLY, and that is what keeps the note above true: `import type` is
// erased at compile time, so this file still imports nothing at runtime and
// still cannot read an uninitialised binding out of `ebooks.ts`.
import type { Draftable } from '@/content/drafts'

export interface Ebook extends Draftable {
  /** Path under `impact-labs/ebooks/`. See the note above on where these come from. */
  slug: string
  /** The title, as the index lists it and as its own page leads with. */
  title: string
  /** The series line above the headline. */
  eyebrow: string
  /** The square cover, as a file that is actually in `public/legacy/`. See `derived()`. */
  cover: { src: string; srcSet: string; alt: string; width: number; height: number }
  /**
   * The imported index's own URL for this cover — `…-400x400.jpg`, the string the
   * WordPress JSON carries. Kept because `splitEbookBlocks` matches the blocks it
   * replaces on it, and it is NOT what renders: no such file is in the repository.
   */
  imported: string
  /** What this e-book is. The series description everywhere today. */
  body: string[]
  /** Where the PDF is. Set for the two the index carried a link to. */
  download?: string
  /**
   * Where the card goes when this e-book already has a page of its own.
   *
   * Two entries: Transform Better, rebuilt as a written page on 2026-08-26
   * (`content/pages/ebook-transform-better.ts`), and Agentic AI, built to
   * digitopia.co's own design on 2026-08-28
   * (`content/pages/ebook-agentic-ai.ts`) — both in the route registry in six
   * locales. Their cards point there and `prerender.tsx` skips them, because
   * generating a second document at the same path would overwrite the designed
   * one with this template.
   */
  href?: string
  /** An unresolved gap, rendered amber in a preview and gating production. */
  check?: string
}

export const EYEBROW = 'Digitopia Impact Labs / E-Books'

/**
 * The series' own two lines, taken verbatim from the index page's lede. They
 * describe the e-book series rather than any one book, which is why they can
 * stand under all of them — and why every page carrying them carries a `check`.
 */
export const SERIES_BODY = [
  'This e-book is one of the Digitopia Impact Labs guides, written for the people who have to carry a transformation rather than for the shelf.',
  'Unlock the power of knowledge. Enhance your digital maturity, sustainability initiatives, and AI capabilities with expert insights and strategic approaches.',
]

/** What is still missing for an e-book, said plainly and in one place. */
export function stillToCome(opts: { slug: string; confirmed?: boolean; download?: boolean }): string {
  const parts = [
    'this e-book’s own summary from digitopia.co — the paragraphs above describe the series, not this book',
  ]
  if (!opts.download) {
    parts.push(
      'a download destination, so the Download the E-Book button is not rendered on this page',
    )
  }
  if (!opts.confirmed) {
    parts.push(
      `confirmation of this page’s address — the slug \`${opts.slug}\` is derived from the title, `
        + 'because the e-book pages were never imported and the redirect table does not know them. '
        + 'Only `agentic-ai`, `transform-better-e-book`, `sustainability-innovation`, '
        + '`importance-of-measurement-for-transformation-success`, `ai-in-fashion-retail` '
        + '`leaders-guide-to-generative-ai` and '
        + '`mastering-the-art-of-digital-transformation` are '
        + 'confirmed against digitopia.co, and if '
        + 'this one differs there, an inbound link to it will 404 after the switch',
    )
  }
  return `Still to be supplied for this e-book: ${parts.join('; ')}.`
}

/**
 * The file that is really on disk for an imported cover URL.
 *
 * `optimize-images.mjs` writes WebP variants beside each imported image and the
 * originals stay out of git (`legacy-src/` is not committed), so
 * `…-400x400.jpg` is a URL the WordPress JSON remembers and **not** a file this
 * site can serve. Every card and every e-book hero was drawing that 404 until
 * 2026-08-30, because `imageVariants()` reads `content/imported/images.json`,
 * which then held one entry and none of these (restored 2026-10-02) — so the
 * manifest lookup missed and the fallback was the missing original.
 *
 * The variants are committed, so this rewrite is the whole fix and costs no new
 * bytes: the 400px file is the imported crop at its native size, and the 384px
 * one is the better fit for a ~300px card.
 */
export function derived(art: string, width: 384 | 400): string {
  return `${art.replace(/\.(jpe?g|png|webp)$/i, '')}-${width}.webp`
}

export function ebook(
  slug: string,
  title: string,
  art: string,
  opts: { confirmed?: boolean; download?: string; href?: string } = {},
): Ebook {
  return {
    slug,
    title,
    eyebrow: EYEBROW,
    imported: art,
    cover: {
      src: derived(art, 400),
      srcSet: `${derived(art, 384)} 384w, ${derived(art, 400)} 400w`,
      alt: `${title} cover`,
      width: 400,
      height: 400,
    },
    body: SERIES_BODY,
    download: opts.download,
    href: opts.href,
    // The one with a page of its own answers all three questions there instead.
    check: opts.href
      ? undefined
      : stillToCome({ slug, confirmed: opts.confirmed, download: Boolean(opts.download) }),
  }
}

export const UPLOADS = '/legacy/wp-content/uploads'

/**
 * Where the PDFs live: the `website-documents` bucket, not `public/`.
 *
 * Same reasoning as `transformBetterEbookPdf` in `content/pages/links.ts` —
 * they are large documents that change on marketing's schedule, and putting
 * them in the repository would put every future revision in the git history
 * forever.
 */
export const BUCKET
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook'

/**
 * One PDF's URL, from the object key exactly as the bucket holds it.
 *
 * The keys carry spaces — "375b51e4-…-Agentic AI - A Practical Guide.pdf" — so
 * the encoding happens here, once, rather than being hand-written into sixteen
 * string literals where a single missed `%20` is invisible until somebody
 * clicks. Only the space needs it; the rest of these keys are already ASCII,
 * apostrophes included (the bucket holds them folded to `_`).
 */
export function pdf(objectKey: string): string {
  return `${BUCKET}/${objectKey.replace(/ /g, '%20')}`
}
